import { useState, useEffect, useRef, Suspense, useCallback } from "react"
import { Canvas, useThree } from "@react-three/fiber"
import {
  OrbitControls,
  Environment,
  PerspectiveCamera,
} from "@react-three/drei"
import * as THREE from "three"
import { CantileverRack } from "@/components/cantilever-rack"
import { CantileverControls } from "@/components/cantilever-controls"
import { Floor } from "@/components/floor"
import { AxisHelper } from "@/components/axis-helper"
import { useCantileverStore } from "@/lib/cantilever-store"
import { useLanguageStore } from "@/lib/language-store"
import { Button } from "@/components/ui/button"
import { Loader as Loader2, RotateCcw, Download, Palette, Layers3, Eclipse, Axis3d as Axis3D } from "lucide-react"
import { AuthHeader } from "@/components/auth-header"

function ScreenshotHelper({ onScreenshot }: { onScreenshot: (fn: () => void) => void }) {
  const { gl, scene, camera } = useThree()
  useEffect(() => {
    onScreenshot(() => {
      gl.render(scene, camera)
      const dataURL = gl.domElement.toDataURL("image/png")
      const link = document.createElement("a")
      link.download = `cantilever-rack-${Date.now()}.png`
      link.href = dataURL
      link.click()
    })
  }, [gl, scene, camera, onScreenshot])
  return null
}

function CanvasInstructions() {
  const { getTranslation } = useLanguageStore()
  return (
    <div className="absolute bottom-2 right-2 z-10 text-[10px] text-gray-600 bg-white/95 px-3 py-2 rounded-xl shadow">
      <p>{getTranslation("canvas.zoom")}</p>
      <p>{getTranslation("canvas.pan")}</p>
      <p>{getTranslation("canvas.rotate")}</p>
    </div>
  )
}

function LoadingSpinner() {
  return (
    <div className="absolute inset-0 flex items-center justify-center bg-gray-100/80 backdrop-blur-sm z-20 rounded-xl">
      <div className="flex flex-col items-center gap-4">
        <Loader2 className="h-12 w-12 animate-spin text-[#212b4f]" />
        <p className="text-lg font-medium text-gray-700">Loading 3D Scene...</p>
      </div>
    </div>
  )
}

export default function CantileverConfigurator() {
  const [isInitialLoading, setIsInitialLoading] = useState(true)
  const screenshotFnRef = useRef<(() => void) | null>(null)
  const cameraRef = useRef<THREE.PerspectiveCamera>(null)
  const controlsRef = useRef<any>(null)
  const { getTranslation } = useLanguageStore()

  const {
    showShadows,
    toggleShadows,
    showFloor,
    toggleFloor,
    gradientBackground,
    toggleGradientBackground,
    showAxisHelper,
    toggleAxisHelper,
    config,
  } = useCantileverStore()

  useEffect(() => {
    const timer = setTimeout(() => setIsInitialLoading(false), 1200)
    return () => clearTimeout(timer)
  }, [])

  // Camera positioned along +X axis (90° Y rotation) looking at the rack from the arm side
  const initialCameraPos: [number, number, number] = [8.0, 4.5, 3.5]
  const initialTarget: [number, number, number] = [0, 2.0, -2.0]

  const resetCamera = () => {
    if (cameraRef.current) {
      cameraRef.current.position.set(...initialCameraPos)
      cameraRef.current.lookAt(...initialTarget)
      cameraRef.current.updateProjectionMatrix()
    }
    if (controlsRef.current) {
      controlsRef.current.target.set(...initialTarget)
      controlsRef.current.update()
    }
  }

  const handleScreenshotRegister = useCallback((fn: () => void) => {
    screenshotFnRef.current = fn
  }, [])

  const handleScreenshot = () => {
    screenshotFnRef.current?.()
  }

  const lightBtn = {
    backgroundColor: "#f8f9fa",
    color: "#212b4f",
    borderColor: "#e9ecef",
    padding: "6px",
    width: "30px",
    height: "30px",
    borderRadius: "10px",
  }

  const activeBtn = { ...lightBtn, backgroundColor: "#212b4f", color: "white" }

  return (
    <div className="flex flex-col md:flex-row gap-3 md:h-screen p-2">
      {/* 3D Canvas */}
      <div className="relative bg-gray-100 overflow-hidden rounded-xl border border-gray-200 shadow-sm h-[60vw] min-h-[280px] max-h-[480px] md:h-auto md:max-h-[100dvh] md:flex-1 min-w-0">
        {isInitialLoading && <LoadingSpinner />}

        <div className="absolute top-4 right-4 z-30">
          <AuthHeader />
        </div>

        <div className="absolute top-4 left-4 z-30 flex gap-2 flex-wrap">
          <Button
            onClick={toggleGradientBackground}
            variant="outline"
            size="icon"
            style={gradientBackground ? activeBtn : lightBtn}
            title={getTranslation("toggle.background")}
          >
            <Palette className="h-3.5 w-3.5" />
          </Button>
          <Button
            onClick={toggleFloor}
            variant="outline"
            size="icon"
            style={showFloor ? activeBtn : lightBtn}
            title={getTranslation("toggle.floor")}
          >
            <Layers3 className="h-3.5 w-3.5" />
          </Button>
          <Button
            onClick={toggleShadows}
            variant="outline"
            size="icon"
            style={showShadows ? activeBtn : lightBtn}
            title={getTranslation("toggle.shadows")}
          >
            <Eclipse className="h-3.5 w-3.5" />
          </Button>
          <Button
            onClick={toggleAxisHelper}
            variant="outline"
            size="icon"
            style={showAxisHelper ? activeBtn : lightBtn}
            title={getTranslation("toggle.axis")}
          >
            <Axis3D className="h-3.5 w-3.5" />
          </Button>
          <Button onClick={handleScreenshot} variant="outline" size="icon" style={lightBtn} title={getTranslation("download.screenshot")}>
            <Download className="h-3.5 w-3.5" />
          </Button>
          <Button onClick={resetCamera} variant="outline" size="icon" style={lightBtn} title={getTranslation("reset.view")}>
            <RotateCcw className="h-3.5 w-3.5" />
          </Button>
        </div>

        <Canvas
          shadows={showShadows}
          dpr={[1, 2]}
          performance={{ min: 0.5 }}
          gl={{ antialias: true, alpha: true, powerPreference: "high-performance", stencil: false, depth: true }}
          frameloop="demand"
          style={{
            background: gradientBackground
              ? "linear-gradient(to bottom, #e5e7eb 0%, white 100%)"
              : "white",
          }}
        >
          <PerspectiveCamera
            makeDefault
            ref={cameraRef}
            position={initialCameraPos}
            fov={45}
            near={0.1}
            far={200}
          />

          <ambientLight intensity={0.5} />
          <directionalLight
            position={[5, 12, 8]}
            intensity={2.0}
            castShadow={showShadows}
            shadow-mapSize-width={2048}
            shadow-mapSize-height={2048}
            shadow-camera-left={-20}
            shadow-camera-right={20}
            shadow-camera-top={20}
            shadow-camera-bottom={-20}
            shadow-camera-near={0.5}
            shadow-camera-far={100}
            shadow-bias={-0.0001}
          />
          <hemisphereLight args={["#ffffff", "#444444", 0.3]} />

          <Suspense fallback={null}>
            <ScreenshotHelper onScreenshot={handleScreenshotRegister} />
            <CantileverRack configuration={config} />
            {showFloor && <Floor position={[0, -0.001, -10]} />}
            <AxisHelper size={1.0} visible={showAxisHelper} />
            <Environment preset="sunset" background={false} />
          </Suspense>

          <OrbitControls
            ref={controlsRef}
            makeDefault
            target={initialTarget}
            enableZoom
            minPolarAngle={0}
            maxPolarAngle={Math.PI / 2}
            minDistance={1}
            maxDistance={100}
            enableDamping={false}
            rotateSpeed={0.7}
            zoomSpeed={1.2}
            panSpeed={1.2}
          />
        </Canvas>

        <CanvasInstructions />
      </div>

      {/* Config Panel */}
      <div className="flex flex-col overflow-auto w-full md:w-auto md:min-w-[360px] md:max-w-[480px] md:h-full">
        <div className="flex-1 overflow-auto">
          <CantileverControls />
        </div>
      </div>
    </div>
  )
}
